/* eslint max-lines: ["error", {"max": 600, "skipComments": true, "skipBlankLines": true }] */
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Box, Button, Menu, MenuItem, Tooltip } from '@mui/material';
import useInfoDialog from 'common/hooks/useInfoDialog';
import useRoles from 'common/hooks/useRoles';
import orderEnum, { orderSource } from 'enums/order.enum';
import { every, filter, includes, isEmpty, map, some } from 'lodash';
import CancelOrderButton from 'modules/PatientView/components/EligibilityActionItems/CancelOrderButton';
import EditOrderAddressButton from 'modules/PatientView/components/EligibilityActionItems/EditOrderAddressButton';
import FillLaterMenuButton from 'modules/PatientView/components/EligibilityActionItems/FillLaterMenuButton';
import ModifyAssociationsButton from 'modules/PatientView/components/EligibilityActionItems/ModifyAssociationButton';
import ReverifyOrderButton from 'modules/PatientView/components/EligibilityActionItems/ReVerifyOrderButton';
import ReshipOrderButton from 'modules/PatientView/components/EligibilityActionItems/ReshipOrderButton';
import ReviewPrescriptionButton from 'modules/PatientView/components/EligibilityActionItems/ReviewPrescriptionButton';
import SendOrderBackToQueueButton from 'modules/PatientView/components/EligibilityActionItems/SendOrderBackToQueueButton';
import SendToShippingButton from 'modules/PatientView/components/EligibilityActionItems/SendToShippingButton';
import ShipLaterButton from 'modules/PatientView/components/EligibilityActionItems/ShipLaterButton';
import TransferOrderButton from 'modules/PatientView/components/EligibilityActionItems/TransferOrderButton';
import VerifyOrderButton from 'modules/PatientView/components/EligibilityActionItems/VerifyOrderButton';
import VoidShipmentButton from 'modules/PatientView/components/EligibilityActionItems/VoidShipmentButton';
import { enqueueSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { markOrderForExternalProcessing } from 'store/thunks/patientThunks';
import { getPartners } from 'store/thunks/partnerThunks';
import useFillLaterActionProps from '../hooks/useFillLaterActionProps';

const OrderActionsMenu = ({
  order,
  patient,
  orderAddress,
  deliveryInstructions,
  events,
  onActionComplete,
  onAddressUpdate,
  enriching,
  reloading,
}) => {
  const dispatch = useDispatch();
  const hasAccess = useRoles();
  const [anchor, setAnchor] = useState(null);

  // POM-6313: close the Actions menu if the order starts refreshing under it — its items were
  // decided from the order as it was when the menu opened.
  useEffect(() => {
    if (reloading) setAnchor(null);
  }, [reloading]);

  const fillLaterProps = useFillLaterActionProps(order);
  const { InfoDialog: ProcessExternalInfoDialog, showInfoDialog: showProcessExternalDialog } =
    useInfoDialog();

  const patientPrescriptions = useSelector(
    ({ patient: patientStore }) => patientStore[order.mpi]?.prescriptions ?? []
  );

  const partners = useSelector(({ partner, auth }) => partner[auth?.currentTenantId]);
  const orderPartner = partners?.find(
    (partnerRecord) => partnerRecord.partnerUuid === order.partner?.partnerUuid
  );
  const isPartnerSuspended = !!orderPartner?.isSuspended;

  useEffect(() => {
    if (!orderPartner) dispatch(getPartners());
  }, [dispatch, orderPartner]);

  const userFacilityIds = useSelector(({ auth }) =>
    map(auth?.fulfillingPharmacies, 'fulfillingPharmacyId')
  );
  const userOrderAccess = includes(userFacilityIds, order.fulfillingPharmacyId);

  const cancelBtnRef = useRef(null);
  const editAddrBtnRef = useRef(null);
  const transferBtnRef = useRef(null);
  const modifyAssocBtnRef = useRef(null);
  const reverifyBtnRef = useRef(null);
  const reshipBtnRef = useRef(null);
  const voidShipBtnRef = useRef(null);
  const sendBackToQueueBtnRef = useRef(null);
  const shipLaterBtnRef = useRef(null);

  const isLineItemsInReprocessingState = some(
    order.lineItems,
    ({ lineItemStatus }) => lineItemStatus === 'RE_PROCESS'
  );
  const isLineItemsInBulkShipmentQueue = some(order.lineItems, { inBatchShipmentQueue: true });
  const batchShipmentId = order.lineItems?.find((li) => li.inBatchShipmentQueue)?.batchShipmentId;
  const isInShipment = some(
    order.lineItems,
    ({ inBatchShipmentQueue, processingState }) =>
      inBatchShipmentQueue && processingState === 'IN_SHIPMENT'
  );
  // POM-6221: a VOID order is parked until its Fill Later date arrives. Order View was written
  // before VOID existed, so none of the status lists below caught it and every action stayed
  // live on it. Modify Association is deliberately left open, as it is on the patient profile:
  // re-pointing an order at the right patient or practice is record correction, not fulfilment.
  const isParkedOrder = order.orderStatus === orderEnum.FILL_LATER;

  const isOrderTerminal = includes(
    [orderEnum.SHIPPED, orderEnum.TRANSFERRED, orderEnum.EXPIRED],
    order.orderStatus
  );
  const hasTerminalLineItem = some(order.lineItems, ({ processingState }) =>
    includes(['SHIPPED', 'TRANSFERRED'], processingState)
  );
  const cancelOrderDisabled =
    isOrderTerminal ||
    hasTerminalLineItem ||
    (isLineItemsInBulkShipmentQueue && order.orderStatus === orderEnum.ADDED_TO_SHIPMENT);
  const cancelOrderTooltip =
    (isOrderTerminal &&
      `Order cannot be canceled because it is ${order.orderStatus.toLowerCase()}`) ||
    (hasTerminalLineItem &&
      'Order cannot be canceled because one or more line items have been shipped or transferred') ||
    (isLineItemsInBulkShipmentQueue &&
      order.orderStatus === orderEnum.ADDED_TO_SHIPMENT &&
      'Order line items in batch shipment cannot be canceled') ||
    undefined;
  const isShipLaterDisabled = includes(
    [orderEnum.ADDED_TO_SHIPMENT, orderEnum.SHIPPED, orderEnum.CANCELED, orderEnum.FILL_LATER],
    order.orderStatus
  );

  const isValidOrder = order.orderType === 'RX_ENTRY' || order.orderType === 'OTC';
  const isManualOrder = orderSource[order.source]?.sourceKey === 'Manual';
  const isRefillOrder = order.fillType === 'REFILL';

  // ── Standalone button visibility ─────────────────────────────────────────

  const showNeedsReviewButton =
    includes(['OPEN', 'ORDER_VERIFICATION', 'ORDER_RE_VERIFICATION'], order.orderStatus) &&
    some(order.lineItems, { taggedForReview: false }) &&
    !order.isProcessedExternal;

  const showVerifyOrderBtn =
    includes([orderEnum.ORDER_VERIFICATION, orderEnum.ORDER_RE_VERIFICATION], order.orderStatus) &&
    hasAccess.verifyOrder &&
    !enriching;

  const displaySendToShipmentButton =
    includes([orderEnum.ORDER_VERIFIED, orderEnum.HOLD], order.orderStatus) &&
    hasAccess.createOrderShipment;

  // ── Actions menu item visibility ─────────────────────────────────────────

  // Reship eligibility: order shipped, user has role + facility access, and at least one
  // line item is actually in SHIPPED processing state (matches PatientOrdersAccordion logic).
  const hasShippedLineItem = !isEmpty(
    filter(order.lineItems, (item) => item.processingState === 'SHIPPED')
  );
  const showReshipmentButton =
    order.orderStatus === orderEnum.SHIPPED &&
    hasAccess.reshipOrder &&
    userOrderAccess &&
    hasShippedLineItem;

  const showVoidShipmentMenuItem = order.orderStatus === orderEnum.SHIPPED;

  // Modify Association: requires role, facility access, and not in a terminal status.
  const showModifyAssociationMenuItem =
    hasAccess.updateOrderAssociation &&
    userOrderAccess &&
    order.orderStatus !== orderEnum.EXPIRED &&
    order.orderStatus !== orderEnum.TRANSFERRED;

  // Transfer Facility: requires role, only in pre-verification statuses, not processed externally.
  const showTransferFacilityMenuItem =
    hasAccess.transferOrder &&
    !order.isProcessedExternal &&
    includes(
      ['OPEN', orderEnum.ORDER_VERIFICATION, orderEnum.ORDER_RE_VERIFICATION],
      order.orderStatus
    );

  const displayReverifyOrderBtn =
    isValidOrder && !order.isProcessedExternal && order.orderStatus === orderEnum.ADDED_TO_SHIPMENT;

  const showProcessExternalMenuItem =
    isValidOrder &&
    hasAccess.markOrderForExternalProcessing &&
    !order.isProcessedExternal &&
    !every(order.lineItems, 'taggedForReview') &&
    includes(['OPEN', 'ORDER_VERIFICATION'], order.orderStatus);

  const processExternalDisabled = isLineItemsInReprocessingState || isManualOrder || isRefillOrder;
  const processExternalTooltip =
    (isManualOrder && 'Manual orders do not support external processing functionality.') ||
    (isRefillOrder && 'Refill orders do not support external processing functionality.') ||
    '';

  const isSendBackInvalidOrderStatus = includes(
    [
      orderEnum.EXPIRED,
      orderEnum.TRANSFERRED,
      orderEnum.SHIPPED,
      orderEnum.CANCELED,
      orderEnum.FILL_LATER,
    ],
    order.orderStatus
  );
  const isBatchedWithShipmentCreated = some(
    order.lineItems,
    ({ inBatchShipmentQueue, processingState }) =>
      inBatchShipmentQueue && processingState === 'SHIPPED'
  );
  const isOrderInReview = some(
    order.lineItems,
    ({ taggedForReview, processingState }) =>
      taggedForReview && !includes(['CANCELED', 'SPLIT'], processingState)
  );
  const isSendBackToQueueDisabled =
    enriching || isSendBackInvalidOrderStatus || isBatchedWithShipmentCreated || isOrderInReview;

  let sendBackToQueueTooltip;
  if (isSendBackInvalidOrderStatus) {
    sendBackToQueueTooltip =
      'This order cannot be sent back to the queue due to its current status';
  } else if (isBatchedWithShipmentCreated) {
    sendBackToQueueTooltip =
      'Order cannot be sent back to the queue as it has line items that are part of a batch shipment that has already been shipped';
  } else if (isOrderInReview) {
    sendBackToQueueTooltip = 'Order cannot be sent back to the queue while it is in review';
  }

  const handleProcessExternal = () => {
    setAnchor(null);
    showProcessExternalDialog({
      title: 'Confirm Processing External',
      message: 'Are you sure you want to mark this order to be processed externally?',
      isWarning: true,
      callback: () =>
        dispatch(markOrderForExternalProcessing({ mpi: order.mpi, orderId: order.orderId })).then(
          (isMarkedReview) => {
            if (isMarkedReview) {
              enqueueSnackbar(`Order ${order.orderId} marked for processing externally`, {
                variant: 'error',
              });
            }
            onActionComplete();
          }
        ),
      cancelable: true,
      confirmButtonText: 'Yes',
    });
  };

  const canEditAddress =
    !!orderAddress &&
    isValidOrder &&
    hasAccess.updateOrderAddress &&
    order.orderStatus !== 'CANCELED' &&
    order.orderStatus !== 'SHIPPED' &&
    order.orderStatus !== 'TRANSFERRED' &&
    order.orderStatus !== orderEnum.EXPIRED &&
    !isParkedOrder &&
    !order.isProcessedExternal;

  return (
    <>
      <ProcessExternalInfoDialog />

      {/* ── Standalone buttons — rendered left of the Actions menu ── */}
      {showVerifyOrderBtn && (
        <VerifyOrderButton
          mpi={order.mpi}
          order={{ ...order, status: order.orderStatus }}
          isManualOrder={isManualOrder}
          isRxEntryOrder={isValidOrder}
          onSuccess={onActionComplete}
          disabled={reloading}
        />
      )}
      {displaySendToShipmentButton && (
        <SendToShippingButton
          mpi={order.mpi}
          orderId={order.orderId}
          orderStatus={order.orderStatus}
          onSuccess={onActionComplete}
          disabled={reloading}
        />
      )}
      {showNeedsReviewButton && (
        <ReviewPrescriptionButton
          disabled={reloading || isLineItemsInReprocessingState}
          patientId={order.mpi}
          orderId={order.orderId}
          lineItems={filter(
            order.lineItems,
            (li) => !includes(['CANCELED', 'SPLIT'], li.processingState)
          )}
          patientPrescriptions={patientPrescriptions}
          onSuccess={onActionComplete}
        />
      )}

      {/* POM-6320: Fill Later stands beside Needs Review rather than sitting inside the Actions
          menu. Parking an order is a decision taken while reading it, and the three scopes are
          already a menu of their own — burying that menu inside another one would put the choice
          two clicks away from the screen where it is made. */}
      {order.mpi && (
        <FillLaterMenuButton
          mpi={order.mpi}
          order={{ ...fillLaterProps.order, shippingAddress: orderAddress }}
          orderDetails={fillLaterProps.orderDetails}
          movableLineItems={fillLaterProps.movableLineItems}
          canMoveToFillLater={fillLaterProps.canMoveToFillLater}
          splitSelectedLineItems={fillLaterProps.splitSelectedLineItems}
          splitSkippedCount={fillLaterProps.splitSkippedCount}
          moveableDialogLineItems={fillLaterProps.moveableDialogLineItems}
          onActionComplete={onActionComplete}
          disabled={reloading}
        />
      )}

      <Button
        size='small'
        variant='outlined'
        endIcon={<MoreVertIcon />}
        onClick={(e) => setAnchor(e.currentTarget)}
        disabled={reloading}
      >
        Actions
      </Button>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Tooltip title={sendBackToQueueTooltip} placement='left' arrow>
          <span>
            <MenuItem
              disabled={isSendBackToQueueDisabled}
              onClick={() => {
                setAnchor(null);
                sendBackToQueueBtnRef.current?.querySelector('button')?.click();
              }}
            >
              Send Order Back To Queue
            </MenuItem>
          </span>
        </Tooltip>
        {displayReverifyOrderBtn && (
          <Tooltip
            title={
              isLineItemsInBulkShipmentQueue
                ? 'Order line items in batch shipment cannot be reverified'
                : undefined
            }
            placement='left'
            arrow
          >
            <span>
              <MenuItem
                disabled={isLineItemsInBulkShipmentQueue}
                onClick={() => {
                  setAnchor(null);
                  reverifyBtnRef.current?.querySelector('button')?.click();
                }}
              >
                Reverify Order
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {showReshipmentButton && (
          <Tooltip
            title={isPartnerSuspended ? 'This partner is suspended.' : undefined}
            placement='left'
            arrow
          >
            <span>
              <MenuItem
                disabled={isPartnerSuspended}
                onClick={() => {
                  setAnchor(null);
                  reshipBtnRef.current?.querySelector('button')?.click();
                }}
              >
                Reship Order
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {canEditAddress && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              editAddrBtnRef.current?.querySelector('button')?.click();
            }}
          >
            Send to different address
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            setAnchor(null);
            shipLaterBtnRef.current?.querySelector('button')?.click();
          }}
          disabled={isShipLaterDisabled}
        >
          Ship Later
        </MenuItem>
        {showTransferFacilityMenuItem && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              transferBtnRef.current?.querySelector('button')?.click();
            }}
          >
            Transfer Facility
          </MenuItem>
        )}
        {showModifyAssociationMenuItem && (
          <MenuItem
            onClick={() => {
              setAnchor(null);
              modifyAssocBtnRef.current?.querySelector('button')?.click();
            }}
          >
            Modify Association
          </MenuItem>
        )}
        {showProcessExternalMenuItem && (
          <Tooltip title={processExternalTooltip} placement='left' arrow>
            <span>
              <MenuItem
                sx={{ color: 'error.main' }}
                disabled={processExternalDisabled}
                onClick={handleProcessExternal}
              >
                Process Externally
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {order.orderStatus !== 'CANCELED' && !isParkedOrder && (
          <Tooltip title={cancelOrderTooltip} placement='left' arrow>
            <span>
              <MenuItem
                sx={{ color: 'error.main' }}
                disabled={cancelOrderDisabled}
                onClick={() => {
                  setAnchor(null);
                  cancelBtnRef.current?.querySelector('button')?.click();
                }}
              >
                Cancel Order
              </MenuItem>
            </span>
          </Tooltip>
        )}
        {showVoidShipmentMenuItem && (
          <MenuItem
            sx={{ color: 'error.main' }}
            onClick={() => {
              setAnchor(null);
              voidShipBtnRef.current?.querySelector('button')?.click();
            }}
          >
            Void Shipment
          </MenuItem>
        )}
      </Menu>

      {/* Hidden trigger wrappers — dialogs are rendered by the child components */}
      <Box sx={{ display: 'none' }}>
        <span ref={editAddrBtnRef}>
          {canEditAddress && (
            <EditOrderAddressButton
              orderId={order.orderId}
              mpi={order.mpi}
              orderAddress={orderAddress}
              deliveryInstructions={deliveryInstructions}
              afterAddressUpdate={async () => {
                await Promise.all([onActionComplete(), onAddressUpdate?.()]);
              }}
              disabled={isLineItemsInReprocessingState}
              notificationPreference={patient?.notificationPreferences}
            />
          )}
        </span>
        <span ref={transferBtnRef}>
          {showTransferFacilityMenuItem && (
            <TransferOrderButton
              orderId={order.orderId}
              mpi={order.mpi}
              orderFacilityId={order.fulfillingPharmacyId}
              lineItems={order.lineItems}
              patientData={patient}
              onBatchTransferComplete={onActionComplete}
            />
          )}
        </span>
        <span ref={modifyAssocBtnRef}>
          {showModifyAssociationMenuItem && (
            <ModifyAssociationsButton
              mpi={order.mpi}
              orderId={order.orderId}
              order={order}
              patientData={patient}
              onSuccess={onActionComplete}
            />
          )}
        </span>
        <span ref={cancelBtnRef}>
          <Tooltip
            title={
              isLineItemsInBulkShipmentQueue && order.orderStatus === orderEnum.ADDED_TO_SHIPMENT
                ? 'Order is part of a batch shipment and cannot be cancelled'
                : undefined
            }
          >
            <span>
              <CancelOrderButton
                orderId={order.orderId}
                mpi={order.mpi}
                hasShipment={events.length > 0}
                onSuccess={onActionComplete}
                disabled={
                  isLineItemsInBulkShipmentQueue &&
                  order.orderStatus === orderEnum.ADDED_TO_SHIPMENT
                }
              />
            </span>
          </Tooltip>
        </span>
        <span ref={reverifyBtnRef}>
          {displayReverifyOrderBtn && (
            <ReverifyOrderButton
              mpi={order.mpi}
              orderId={order.orderId}
              disabled={isLineItemsInBulkShipmentQueue}
              onSuccess={onActionComplete}
            />
          )}
        </span>
        <span ref={reshipBtnRef}>
          {showReshipmentButton && (
            <ReshipOrderButton
              mpi={order.mpi}
              orderId={order.orderId}
              lineItems={order.lineItems}
              partnerUuid={order.partner?.partnerUuid}
              onSuccess={onActionComplete}
            />
          )}
        </span>
        <span ref={voidShipBtnRef}>
          {showVoidShipmentMenuItem && (
            <VoidShipmentButton
              mpi={order.mpi}
              orderId={order.orderId}
              lineItems={order.lineItems}
              order={order}
              onSuccess={onActionComplete}
            />
          )}
        </span>
        <span ref={sendBackToQueueBtnRef}>
          <SendOrderBackToQueueButton
            mpi={order.mpi}
            orderId={order.orderId}
            disabled={isSendBackToQueueDisabled}
            onSuccess={onActionComplete}
            isInBatch={isLineItemsInBulkShipmentQueue}
            batchShipmentId={batchShipmentId}
            isInShipment={isInShipment}
          />
        </span>
        <span ref={shipLaterBtnRef}>
          <ShipLaterButton
            mpi={order.mpi}
            orderId={order.orderId}
            disabled={isShipLaterDisabled}
            onSuccess={onActionComplete}
          />
        </span>
        <span ref={shipLaterBtnRef}>
          <ShipLaterButton
            mpi={order.mpi}
            orderId={order.orderId}
            disabled={isShipLaterDisabled}
            onSuccess={onActionComplete}
          />
        </span>
      </Box>
    </>
  );
};

OrderActionsMenu.propTypes = {
  order: PropTypes.shape({
    orderId: PropTypes.string,
    mpi: PropTypes.string,
    orderStatus: PropTypes.string,
    orderType: PropTypes.string,
    fillType: PropTypes.string,
    source: PropTypes.number,
    isProcessedExternal: PropTypes.bool,
    fulfillingPharmacyId: PropTypes.string,
    partnerName: PropTypes.string,
    partner: PropTypes.shape({
      partnerUuid: PropTypes.string,
    }),
    lineItems: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  patient: PropTypes.shape({
    notificationPreferences: PropTypes.shape({
      phone: PropTypes.bool,
      email: PropTypes.bool,
    }),
  }),
  orderAddress: PropTypes.shape({}),
  deliveryInstructions: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  events: PropTypes.arrayOf(PropTypes.shape({})),
  onActionComplete: PropTypes.func.isRequired,
  onAddressUpdate: PropTypes.func,
  enriching: PropTypes.bool,
  reloading: PropTypes.bool,
};

OrderActionsMenu.defaultProps = {
  patient: null,
  orderAddress: null,
  deliveryInstructions: null,
  events: [],
  onAddressUpdate: undefined,
  enriching: false,
  reloading: false,
};

export default OrderActionsMenu;
